import * as React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Field, FieldGroup, FieldLabel } from '../../components/ui/field'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { MeController } from '../../controllers/accountant'
import { AppState } from '../../store'
import { setCurrentAccountant } from '../../store/authentication/actions'
import { AccountantCurrentUser } from '../../types'

interface IStateToProps {
  currentAccountant: AccountantCurrentUser
}

interface IDispatchToProps {
  setCurrentAccountant: (accountant: AccountantCurrentUser) => void
}

const Account = ({ currentAccountant, setCurrentAccountant }: IStateToProps & IDispatchToProps) => {
  const { t } = useTranslation()

  const [firstName, setFirstName] = React.useState(currentAccountant.first_name || '')
  const [lastName, setLastName] = React.useState(currentAccountant.last_name || '')
  const [companyName, setCompanyName] = React.useState(currentAccountant.company_name || '')
  const [locale, setLocale] = React.useState(currentAccountant.locale || 'en')
  const [isSavingProfile, setIsSavingProfile] = React.useState(false)

  const [currentPassword, setCurrentPassword] = React.useState('')
  const [newPassword, setNewPassword] = React.useState('')
  const [isSavingPassword, setIsSavingPassword] = React.useState(false)

  const localeItems = [
    { label: 'English', value: 'en' },
    { label: 'Nederlands', value: 'nl' },
    { label: 'Français', value: 'fr' },
  ]

  const showErrors = (errors: { message: string }[]) => toast.error(errors.map(error => error.message).join(', '))

  const onProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSavingProfile(true)
    try {
      const response = await MeController.update({ first_name: firstName, last_name: lastName, company_name: companyName, locale })
      if (response.errors) {
        showErrors(response.errors)
        return
      }
      setCurrentAccountant(response)
      toast.success(t('Accountant::Profile updated'))
      if (locale !== currentAccountant.locale) window.location.reload()
    } catch (error) {
      toast.error(t('Accountant::Something went wrong, please try again later.'))
    } finally {
      setIsSavingProfile(false)
    }
  }

  const onPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSavingPassword(true)
    try {
      const response = await MeController.update({ current_password: currentPassword, password: newPassword })
      if (response.errors) {
        showErrors(response.errors)
        return
      }
      setCurrentAccountant(response)
      toast.success(t('Accountant::Password updated'))
      setCurrentPassword('')
      setNewPassword('')
    } catch (error) {
      toast.error(t('Accountant::Something went wrong, please try again later.'))
    } finally {
      setIsSavingPassword(false)
    }
  }

  return (
    <>
      <Helmet><title>{`Bizzey | ${t('Accountant::Account')}`}</title></Helmet>

      <div className='flex max-w-2xl flex-col gap-6'>
        <Card>
          <CardHeader>
            <CardTitle>{t('Accountant::Profile')}</CardTitle>
            <CardDescription>{currentAccountant.email}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onProfileSubmit}>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor='first-name'>{t('Accountant::First name')}</FieldLabel>
                  <Input id='first-name' value={firstName} onChange={e => setFirstName(e.currentTarget.value)} />
                </Field>
                <Field>
                  <FieldLabel htmlFor='last-name'>{t('Accountant::Last name')}</FieldLabel>
                  <Input id='last-name' value={lastName} onChange={e => setLastName(e.currentTarget.value)} />
                </Field>
                <Field>
                  <FieldLabel htmlFor='company-name'>{t('Accountant::Company')}</FieldLabel>
                  <Input id='company-name' value={companyName} onChange={e => setCompanyName(e.currentTarget.value)} />
                </Field>
                <Field>
                  <FieldLabel>{t('Accountant::Language')}</FieldLabel>
                  <Select items={localeItems} value={locale} onValueChange={(value: string) => setLocale(value)}>
                    <SelectTrigger className='w-full'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {localeItems.map(item => (
                        <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <div>
                  <Button type='submit' disabled={isSavingProfile}>{t('Accountant::Save')}</Button>
                </div>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('Accountant::Password')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onPasswordSubmit}>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor='current-password'>{t('Accountant::Current password')}</FieldLabel>
                  <Input id='current-password' type='password' required autoComplete='current-password' value={currentPassword} onChange={e => setCurrentPassword(e.currentTarget.value)} />
                </Field>
                <Field>
                  <FieldLabel htmlFor='new-password'>{t('Accountant::New password')}</FieldLabel>
                  <Input id='new-password' type='password' required minLength={8} autoComplete='new-password' value={newPassword} onChange={e => setNewPassword(e.currentTarget.value)} />
                </Field>
                <div>
                  <Button type='submit' disabled={isSavingPassword}>{t('Accountant::Change password')}</Button>
                </div>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

const mapStateToProps = (state: AppState): IStateToProps => ({
  currentAccountant: state.authentication.currentAccountant,
})

const mapDispatchToProps = (dispatch: Dispatch): IDispatchToProps => ({
  setCurrentAccountant: (accountant) => dispatch(setCurrentAccountant(accountant) as any),
})

export default connect(mapStateToProps, mapDispatchToProps)(Account)
